import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Fab,
  Paper,
  Button,
  Select,
  MenuItem,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import CancelIcon from "@mui/icons-material/Cancel";
import MovieIcon from "@mui/icons-material/Movie";
import { useDropzone } from "react-dropzone";

const featureOptions = [
  { key: "conversion", label: "Video Conversion", icon: <SettingsIcon fontSize="large" />, required: true },
  { key: "transcription", label: "Transcription", icon: <VideoLibraryIcon fontSize="large" />, requiresSettings: true },
  { key: "redaction", label: "Redaction", icon: <VisibilityOffIcon fontSize="large" />, requiresSettings: true },
  { key: "aiSummary", label: "AI Summary", icon: <AutoAwesomeIcon fontSize="large" />, requiresSettings: true, requires: "transcription" },
  { key: "objectRecognition", label: "Object Recognition", icon: <SearchIcon fontSize="large" /> },
];

const Profile = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState(["conversion"]); // Conversion is always required
  const [featureSettings, setFeatureSettings] = useState({
    transcription: { language: "en" },
    redaction: { description: "", blurType: "pixelate" },
    aiSummary: { method: "audio" },
  });

  const toggleFeature = (key, requires) => {
    setSelectedFeatures((prev) => {
      let newFeatures = [...prev];

      if (newFeatures.includes(key)) {
        newFeatures = newFeatures.filter((feature) => feature !== key);
        if (key === "transcription") {
          newFeatures = newFeatures.filter((feature) => feature !== "aiSummary"); // Remove AI Summary if Transcription is removed
        }
      } else {
        if (requires && !newFeatures.includes(requires)) return prev; // Prevent selecting AI Summary without Transcription
        newFeatures.push(key);
      }
      return newFeatures;
    });
  };

  const handleSettingChange = (feature, field, value) => {
    setFeatureSettings((prev) => ({
      ...prev,
      [feature]: { ...prev[feature], [field]: value },
    }));
  };

  const getSteps = () => {
    return [{ label: "Upload Video", icon: <CloudUploadIcon fontSize="large" /> }].concat(
      selectedFeatures.map((feature) => {
        const featureData = featureOptions.find((f) => f.key === feature);
        return { label: featureData.label, icon: featureData.icon };
      })
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "video/mp4,video/mov",
    onDrop: (acceptedFiles) => {
      setSelectedFile(acceptedFiles[0]);
      setActiveStep(1);
    },
  });

  const steps = getSteps();

  return (
    <div class="page-container">
      <h1 class="page-title">Evidence Tools</h1>
        <Box sx={{ maxWidth: 800, mx: "auto", textAlign: "center", mt: 5 }}>

        <div style={{marginBottom: "80px"}}>

        {/* Drag & Drop Upload */}
        {!selectedFile ? (
            <Paper
            {...getRootProps()}
            sx={{
                p: 4,
                border: "2px dashed #1976d2",
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: isDragActive ? "#e3f2fd" : "white",
                transition: "0.3s",
            }}
            >
            <input {...getInputProps()} />
            <CloudUploadIcon fontSize="large" color="primary" />
            <Typography variant="h6" sx={{ mt: 2 }}>
                Drag & drop a video here, or click to select
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Supports .mp4 and .mov formats
            </Typography>
            </Paper>
        ) : (
            <Paper 
            elevation={3} 
            sx={{ display: "flex", alignItems: "center", p: 2, borderRadius: 2, mt: 3, width: "100%", maxWidth: 400, mx: "auto", justifyContent: "space-between" }}
          >
            {/* Thumbnail / Video Icon */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                <MovieIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              </Box>
            </Box>
        
            {/* Remove File Button */}
            <IconButton onClick={() => setSelectedFile(null)} color="error">
              <CancelIcon />
            </IconButton>
          </Paper>
        )}

        {/* Feature Selection */}
        <Box sx={{ mt: 4 }}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
            {featureOptions.map(({ key, label, icon, required, requires }) => (
                <Grid item xs={12} sm={4} key={key}>
                <Card
                    sx={{
                    textAlign: "center",
                    cursor: selectedFile ? "pointer" : "not-allowed",
                    p: 2,
                    transition: "0.3s",
                    border: selectedFeatures.includes(key) ? "2px solid #1976d2" : "1px solid #ccc",
                    backgroundColor: selectedFeatures.includes(key) ? "#e3f2fd" : "white",
                    opacity: selectedFile || required ? 1 : 0.5,
                    }}
                    onClick={() => selectedFile && !required && toggleFeature(key, requires)}
                >
                    <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>{icon}</Box>
                    <Typography variant="h6">{label}</Typography>
                    </CardContent>
                </Card>
                </Grid>
            ))}
            </Grid>
        </Box>

        {selectedFeatures.includes("transcription") && (
  <Card elevation={3} sx={{ mt: 3, borderRadius: 2 }}>
    <CardContent>
      <Typography class="feature-setting-title" variant="h6" gutterBottom>Transcription Settings</Typography>
      <Grid container spacing={2} alignItems="left">
        <Grid item xs={6}>
          <FormControl size="small" variant="filled" fullWidth alignItems>
            <InputLabel>Source Language</InputLabel>
            <Select
              value={featureSettings.transcription.language}
              onChange={(e) => handleSettingChange("transcription", "language", e.target.value)}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
            <FormControl size="small" variant="filled" fullWidth>
                <InputLabel>Number of speakers</InputLabel>
                <Select
                value={featureSettings.transcription.targetLanguage}
                onChange={(e) => handleSettingChange("transcription", "numberOfSpeakers", e.target.value)}
                >
                <MenuItem value="1">One</MenuItem>
                <MenuItem value="2">Two</MenuItem>
                <MenuItem value="3">Three</MenuItem>
                </Select>
            </FormControl>
            </Grid>
      </Grid>
    </CardContent>
  </Card>
)}

{selectedFeatures.includes("redaction") && (
  <Card elevation={3} sx={{ mt: 3, borderRadius: 2 }}>
    <CardContent>
      <Typography class="feature-setting-title" variant="h6" gutterBottom>Redaction Settings</Typography>
      <Grid container spacing={2} alignItems="center">
        {/* Description Input */}
        <Grid item xs={8}>
          <TextField
            variant="filled"
            label="What to Redact"
            placeholder="ex: "
            size="small"
            fullWidth
            value={featureSettings.redaction.description}
            onChange={(e) => handleSettingChange("redaction", "description", e.target.value)}
          />
        </Grid>
        
        {/* Blur Type Dropdown */}
        <Grid item xs={4}>
          <FormControl size="small" variant="filled" fullWidth>
            <InputLabel>Blur Type</InputLabel>
            <Select
              value={featureSettings.redaction.blurType}
              onChange={(e) => handleSettingChange("redaction", "blurType", e.target.value)}
            >
              <MenuItem value="pixelate">Pixelate</MenuItem>
              <MenuItem value="blur">Blur</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
)}
</div>
      {/* Fixed Bottom Stepper */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, height: "90px", width: "100%", backgroundColor: "white", boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)", p: 2, zIndex: 1000 }}>
        {/* <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel icon={step.icon}>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper> */}

        {/* Floating Upload Button */}
        {selectedFile && (
          <Fab color="primary" variant="extended" sx={{ position: "absolute", bottom: 20, right: 20, fontSize: "1rem", p: "10px 20px", boxShadow: 3 }}>
            <CloudUploadIcon sx={{ mr: 1 }} />
            Upload & Process
          </Fab>
        )}
        </Box>
      
        </Box>
        </div>
  );
};

export default Profile;
